import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Check, ShieldCheck, Video, X } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

function formatSlot(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

export default function Admin() {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState("queue");
  const [bookings, setBookings] = useState(null);
  const [members, setMembers] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([api.get("/admin/verifications"), api.get("/admin/members")]);
      setBookings(b.data.bookings);
      setMembers(m.data.members);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (booking, decision) => {
    setBusyId(booking.id);
    try {
      await api.post(`/admin/verifications/${booking.id}/decision`, {
        decision,
        notes: notes[booking.id] || "",
      });
      toast.success(
        decision === "approved"
          ? `${booking.member.name} is now video-verified.`
          : `${booking.member.name}'s verification was declined.`
      );
      setCallFor(null);
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const queue = (bookings || []).filter((b) => b.status === "scheduled");
  const past = (bookings || []).filter((b) => b.status !== "scheduled");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <p className="eyebrow mb-2">Moderator command center</p>
      <h1 className="font-display text-3xl sm:text-4xl text-stone-900 mb-8">Trust &amp; Safety Console</h1>

      <div className="flex gap-2 mb-8">
        {[
          ["queue", `Interview queue (${queue.length})`],
          ["history", `Decided (${past.length})`],
          ["members", `Members (${members?.length ?? 0})`],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              tab === key ? "bg-[#1F3A34] text-[#FAF8F5]" : "bg-white border border-[#E7DFD5] text-stone-600 hover:border-[#C58342]"
            }`}
            data-testid={`admin-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab !== "members" && (
        <div className="space-y-4">
          {(tab === "queue" ? queue : past).length === 0 && (
            <div className="card-surface p-10 text-center text-stone-500" data-testid="admin-queue-empty">
              {tab === "queue" ? "No interviews waiting. All caught up." : "No decisions made yet."}
            </div>
          )}
          {(tab === "queue" ? queue : past).map((b) => (
            <div key={b.id} className="card-surface overflow-hidden" data-testid={`admin-booking-card-${b.id}`}>
              <div className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#F3EFEA] shrink-0">
                  {b.member.photo_url ? (
                    <img src={b.member.photo_url} alt={b.member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-xl text-stone-300">
                      {b.member.name?.[0]}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-stone-800">{b.member.name}</p>
                    {b.status === "scheduled" && <span className="badge-pending">Scheduled</span>}
                    {b.status === "approved" && <span className="badge-verified"><BadgeCheck size={11} /> Verified</span>}
                    {b.status === "rejected" && <span className="badge-rejected">Rejected</span>}
                  </div>
                  <p className="text-xs text-stone-500 mt-1">
                    {b.member.email} · {b.member.age ? `${b.member.age} yrs · ` : ""}{b.member.location || "Location not set"}
                  </p>
                  <p className="text-sm text-stone-600 mt-1 font-dm">{formatSlot(b.slot)}</p>
                  {b.notes && <p className="text-xs text-stone-500 mt-1 italic">Note: {b.notes}</p>}
                </div>
                {b.status === "scheduled" && (
                  <div className="flex flex-col sm:flex-row gap-2 lg:items-center">
                    <input
                      value={notes[b.id] || ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [b.id]: e.target.value }))}
                      placeholder="Verification notes…"
                      className="rounded-xl border border-[#E7DFD5] px-3 py-2 text-sm w-full sm:w-52"
                      data-testid={`admin-notes-input-${b.id}`}
                    />
                    <button onClick={() => setCallFor(callFor?.id === b.id ? null : b)} className="btn-secondary !px-4 !py-2 text-sm" data-testid="admin-join-interview-btn">
                      <Video size={14} /> {callFor?.id === b.id ? "Close room" : "Join call"}
                    </button>
                    <button
                      onClick={() => decide(b, "approved")}
                      disabled={busyId === b.id}
                      className="btn-primary !px-4 !py-2 text-sm"
                      data-testid="admin-verification-approve-btn"
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      onClick={() => decide(b, "rejected")}
                      disabled={busyId === b.id}
                      className="bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] hover:bg-[#FECACA] font-medium transition-colors px-4 py-2 rounded-xl text-sm inline-flex items-center gap-1.5"
                      data-testid="admin-verification-reject-btn"
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                )}
              </div>
              {callFor?.id === b.id && (
                <div className="bg-stone-950" data-testid="admin-video-call-room">
                  <iframe
                    src={b.meeting_url}
                    title={`Interview with ${b.member.name}`}
                    className="w-full h-[460px] border-0"
                    allow="camera; microphone; fullscreen; display-capture; autoplay"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "members" && (
        <div className="card-surface overflow-x-auto" data-testid="admin-members-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E7DFD5] text-left">
                <th className="px-5 py-3 text-xs font-dm uppercase tracking-wider text-stone-500">Member</th>
                <th className="px-5 py-3 text-xs font-dm uppercase tracking-wider text-stone-500">Location</th>
                <th className="px-5 py-3 text-xs font-dm uppercase tracking-wider text-stone-500">Verification</th>
                <th className="px-5 py-3 text-xs font-dm uppercase tracking-wider text-stone-500">Membership</th>
              </tr>
            </thead>
            <tbody>
              {(members || []).map((m) => (
                <tr key={m.id} className="border-b border-[#E7DFD5]/60 last:border-0">
                  <td className="px-5 py-3">
                    <p className="font-medium text-stone-800">{m.name}</p>
                    <p className="text-xs text-stone-400">{m.email}</p>
                  </td>
                  <td className="px-5 py-3 text-stone-600">{m.profile?.location || "—"}</td>
                  <td className="px-5 py-3">
                    {m.verification_status === "verified" && <span className="badge-verified"><ShieldCheck size={11} /> Verified</span>}
                    {m.verification_status === "scheduled" && <span className="badge-pending">Scheduled</span>}
                    {m.verification_status === "unverified" && <span className="badge-pending">Unverified</span>}
                    {m.verification_status === "rejected" && <span className="badge-rejected">Rejected</span>}
                  </td>
                  <td className="px-5 py-3 text-stone-600 capitalize">{m.membership || "free"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
